/**
 * StageEditorContainer — data + state wiring for the pipeline Stage editor
 * (SE-5488).
 *
 * Subscribes to the editor reactive var (so any toolbar can open it), fetches
 * the board's stages + cards + rules (all skipped while closed), and drives the
 * five editor actions through {@link useStageEditor} over the generated phase
 * mutations + the automation-rule enable primitive. Owns the transient editor
 * UI state (inline-rename target, add-stage draft, per-row busy set, the pending
 * delete plan) and resets it on close — no reset effect (the repo's
 * set-state-in-effect rule).
 * @remarks Container component — owns queries, mutations, and editor state.
 */

import { memo, useCallback, useMemo, useRef, useState } from "react";

import {
  type CreateKanbanPhaseInput,
  type KanbanPipelineTemplate,
  type UpdateKanbanPhaseInput,
  type UpdateKanbanPhasePositionsInput,
  useCreateKanbanPhaseMutation,
  useDeleteKanbanPhaseMutation,
  useGetKanbanBoardQuery,
  useListKanbanCardsQuery,
  useListKanbanPhasesQuery,
  usePipelineAutomationRulesQuery,
  useUpdateKanbanPhaseMutation,
  useUpdateKanbanPhasePositionsMutation,
} from "@/generated/graphql";
import { useToastNotification } from "@/hooks/useToastNotification";

import { AUTOMATION_RULES_PAGE_SIZE, MESSAGES } from "../../../constants";
import { stageGlossaryFor } from "../../../constants/pipelineTemplates";
import { useAutomationRuleCrud } from "../../../hooks/useAutomationRuleCrud";
import { useStageEditor } from "../../../hooks/useStageEditor";
import { useStageEditorPanelState } from "../../../hooks/useStageEditorPanelState";
import {
  buildStageProtectionNotice,
  countCardsInStage,
  type EditableStage,
  findRulesReferencingStage,
  isStaleStageEditorTarget,
  nudgeStage,
  type StageCardRef,
  type StageDeletionPlan,
  sortStagesByPosition,
} from "../../../utils/stageEditorOps";
import StageEditorView, {
  type StageEditorDeleteState,
  type StageEditorRowData,
} from "./StageEditorView";

/**
 * Occupancy-count ceiling for the delete guard.
 *
 * The editor deliberately fetches a single large page of cards to count a
 * stage's occupancy in one shot, rather than sharing the board's paginated
 * `CARDS_PAGE_SIZE` (40) walk. This is the SE-5058 "justified exception": a
 * dedicated `$first` for a self-contained one-shot occupancy read, at the cost
 * of a separate normalized cache entry. It is NOT named `CARDS_PAGE_SIZE` so it
 * cannot be mistaken for — or shadow — the shared paginated constant.
 *
 * Boards larger than this ceiling report `pageInfo.hasNextPage`, which the
 * container feeds to the delete guard as `cardsComplete = false` so an
 * apparently-empty stage fails closed (never deletable while occupancy is
 * unproven) instead of trusting a truncated count.
 */
const STAGE_EDITOR_CARDS_PAGE_SIZE = 1000;

/** Props for {@link StageEditorContainer}. */
export interface StageEditorContainerProps {
  /**
   * The board the screen is currently showing. When the editor's reactive var
   * is still bound to a previously-selected board (a board switch while the
   * editor stayed open), the editor renders closed and skips its queries so it
   * never operates on the stale board (SE-5488 review).
   */
  readonly activeBoardId?: string;
  /**
   * The open board's recruitment motion, forwarded to `useStageEditor` for the
   * SE-5508 `pipeline_stage_edited` telemetry only. Nothing in the editor's
   * behaviour branches on it.
   */
  readonly pipelineTemplate?: KanbanPipelineTemplate | null;
}

/** The pending-delete target, held while the confirm dialog is open. */
interface PendingDelete {
  readonly stageId: string;
  readonly stageName: string;
  readonly plan: StageDeletionPlan;
}

/**
 * Container for the Stage editor.
 * @param props - {@link StageEditorContainerProps}.
 * @param props.activeBoardId - The board the screen is currently showing.
 * @param props.pipelineTemplate - The open board's recruitment motion (telemetry only).
 * @returns The wired {@link StageEditorView}.
 */
const StageEditorContainer = ({
  activeBoardId,
  pipelineTemplate,
}: StageEditorContainerProps) => {
  const toast = useToastNotification();
  const { state, close } = useStageEditorPanelState();
  const boardId = state.kanbanBoardId ?? "";
  // A board switch leaves the (module-level) editor var bound to the old board.
  // Treat that as closed: render nothing and skip all queries/mutations so the
  // editor can never operate on a stale board (SE-5488 review).
  const isStale = isStaleStageEditorTarget(
    state.open,
    state.kanbanBoardId,
    activeBoardId
  );
  const isEditorOpen = state.open && !isStale;
  const skip = !state.open || !boardId || isStale;

  const [editingStageId, setEditingStageId] = useState<string | null>(null);
  const [editNameValue, setEditNameValue] = useState("");
  const [addName, setAddName] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(new Set());
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(
    null
  );
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const phasesQuery = useListKanbanPhasesQuery({
    variables: { kanbanBoardId: boardId },
    skip,
    fetchPolicy: "cache-and-network",
  });
  const cardsQuery = useListKanbanCardsQuery({
    variables: { kanbanBoardId: boardId, first: STAGE_EDITOR_CARDS_PAGE_SIZE },
    skip,
    fetchPolicy: "cache-and-network",
  });
  const rulesQuery = usePipelineAutomationRulesQuery({
    variables: { kanbanBoardId: boardId, first: AUTOMATION_RULES_PAGE_SIZE },
    skip,
    fetchPolicy: "cache-and-network",
  });
  // SE-6554: the header names the pipeline being reshaped, exactly as the Rules
  // modal does — a modal covers the board, so nothing else on screen says which
  // pipeline these stages belong to.
  const boardQuery = useGetKanbanBoardQuery({
    variables: { id: boardId },
    skip,
  });

  const [updatePhaseMutation] = useUpdateKanbanPhaseMutation();
  const [createPhaseMutation] = useCreateKanbanPhaseMutation();
  const [deletePhaseMutation] = useDeleteKanbanPhaseMutation();
  const [updatePositionsMutation] = useUpdateKanbanPhasePositionsMutation();
  const { setRuleEnabled } = useAutomationRuleCrud(boardId);

  const stages = useMemo<EditableStage[]>(
    () =>
      sortStagesByPosition(phasesQuery.data?.listKanbanPhases ?? []).map(
        phase => ({
          id: phase.id,
          name: phase.name,
          position: phase.position,
          isProtected: phase.isProtected,
          isTerminal: phase.isTerminal,
          timeTargetDays: phase.timeTargetDays ?? null,
        })
      ),
    [phasesQuery.data]
  );

  const cards = useMemo<StageCardRef[]>(
    () =>
      (cardsQuery.data?.listKanbanCards.edges ?? []).map(edge => ({
        kanbanPhaseId: edge.node.kanbanPhaseId ?? null,
      })),
    [cardsQuery.data]
  );

  const rules = useMemo(
    () =>
      (rulesQuery.data?.pipelineAutomationRules.edges ?? []).map(
        edge => edge.node
      ),
    [rulesQuery.data]
  );

  // Completeness gates for the delete guard. A set is "complete" only once its
  // data has loaded AND its connection reports no further pages — so before the
  // first page resolves (or beyond the occupancy ceiling / the rules page size)
  // the guard fails closed / degrades to a generic warning rather than trusting
  // a partial view (SE-5488 review).
  const cardsPageInfo = cardsQuery.data?.listKanbanCards.pageInfo;
  const cardsComplete = cardsPageInfo != null && !cardsPageInfo.hasNextPage;
  const rulesPageInfo = rulesQuery.data?.pipelineAutomationRules.pageInfo;
  const rulesComplete = rulesPageInfo != null && !rulesPageInfo.hasNextPage;

  const updatePhase = useCallback(
    (id: string, input: UpdateKanbanPhaseInput) =>
      updatePhaseMutation({
        variables: { id, input },
        refetchQueries: ["ListKanbanPhases"],
        awaitRefetchQueries: true,
      }),
    [updatePhaseMutation]
  );
  const createPhase = useCallback(
    (input: CreateKanbanPhaseInput) =>
      createPhaseMutation({
        variables: { input },
        refetchQueries: ["ListKanbanPhases"],
        awaitRefetchQueries: true,
      }),
    [createPhaseMutation]
  );
  const deletePhase = useCallback(
    (id: string) =>
      deletePhaseMutation({
        variables: { id },
        refetchQueries: ["ListKanbanPhases", "ListKanbanCards"],
        awaitRefetchQueries: true,
      }),
    [deletePhaseMutation]
  );
  const updatePhasePositions = useCallback(
    (input: UpdateKanbanPhasePositionsInput) =>
      updatePositionsMutation({
        variables: { input },
        refetchQueries: ["ListKanbanPhases"],
        awaitRefetchQueries: true,
      }),
    [updatePositionsMutation]
  );

  const editor = useStageEditor({
    kanbanBoardId: boardId,
    stages,
    cards,
    cardsComplete,
    rules,
    rulesComplete,
    updatePhase,
    createPhase,
    deletePhase,
    updatePhasePositions,
    setRuleEnabled,
    pipelineTemplate,
  });

  const markBusy = useCallback((id: string, active: boolean) => {
    setBusyIds(prev =>
      active
        ? new Set([...prev, id])
        : new Set([...prev].filter(existing => existing !== id))
    );
  }, []);

  const runRowWrite = useCallback(
    async (stageId: string, write: () => Promise<unknown>) => {
      markBusy(stageId, true);
      try {
        await write();
      } catch {
        toast.show({ action: "danger", description: MESSAGES.ERROR.GENERIC });
      } finally {
        markBusy(stageId, false);
      }
    },
    [markBusy, toast]
  );

  const handleClose = useCallback(() => {
    setEditingStageId(null);
    setEditNameValue("");
    setAddName("");
    setPendingDelete(null);
    setIsConfirmingDelete(false);
    close();
  }, [close]);

  const handleStartRename = useCallback(
    (stageId: string) => {
      const stage = stages.find(candidate => candidate.id === stageId);
      setEditingStageId(stageId);
      setEditNameValue(stage?.name ?? "");
    },
    [stages]
  );

  const handleSaveRename = useCallback(
    async (stageId: string) => {
      const trimmed = editNameValue.trim();
      if (trimmed.length === 0) return;
      setEditingStageId(null);
      await runRowWrite(stageId, () => editor.renameStage(stageId, trimmed));
    },
    [editNameValue, editor, runRowWrite]
  );

  const handleCancelRename = useCallback(() => {
    setEditingStageId(null);
    setEditNameValue("");
  }, []);

  const handleToggleProtected = useCallback(
    (stageId: string, next: boolean) =>
      runRowWrite(stageId, () => editor.setStageProtected(stageId, next)),
    [editor, runRowWrite]
  );

  const handleToggleTerminal = useCallback(
    (stageId: string, next: boolean) =>
      runRowWrite(stageId, () => editor.setStageTerminal(stageId, next)),
    [editor, runRowWrite]
  );

  const handleChangeTarget = useCallback(
    (stageId: string, next: number | null) => {
      // No-op when the value is unchanged so a blur without an edit never fires
      // a redundant UpdateKanbanPhase (SE-5497).
      const current =
        stages.find(s => s.id === stageId)?.timeTargetDays ?? null;
      if (current === next) return;
      return runRowWrite(stageId, () =>
        editor.setStageTimeTarget(stageId, next)
      );
    },
    [stages, editor, runRowWrite]
  );

  const handleMove = useCallback(
    (stageId: string, direction: "up" | "down") => {
      const orderedIds = nudgeStage(stages, stageId, direction).map(s => s.id);
      return runRowWrite(stageId, () => editor.reorderStages(orderedIds));
    },
    [stages, editor, runRowWrite]
  );

  const handleMoveUp = useCallback(
    (stageId: string) => handleMove(stageId, "up"),
    [handleMove]
  );
  const handleMoveDown = useCallback(
    (stageId: string) => handleMove(stageId, "down"),
    [handleMove]
  );

  const handleDelete = useCallback(
    (stageId: string) => {
      const stage = stages.find(candidate => candidate.id === stageId);
      const plan = editor.previewDeletion(stageId);
      if (!stage || !plan) return;
      setPendingDelete({ stageId, stageName: stage.name, plan });
    },
    [stages, editor]
  );

  const handleCancelDelete = useCallback(() => {
    setPendingDelete(null);
    setIsConfirmingDelete(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    // A blocked plan cannot be confirmed (the dialog's confirm is disabled) —
    // Close simply dismisses it. This covers both the has-cards block and the
    // occupancy-unknown fail-closed state.
    if (
      pendingDelete.plan.status === "blocked-has-cards" ||
      pendingDelete.plan.status === "blocked-unknown-occupancy"
    ) {
      setPendingDelete(null);
      return;
    }
    setIsConfirmingDelete(true);
    try {
      const outcome = await editor.confirmDeletion(pendingDelete.stageId);
      if (outcome.ok) {
        toast.show({
          action: "success",
          description: MESSAGES.SUCCESS.DELETED,
        });
        setPendingDelete(null);
      } else {
        toast.show({
          action: "danger",
          description: `Couldn't pause “${outcome.failedRule.name}”. The stage was not deleted.`,
        });
      }
    } catch {
      toast.show({ action: "danger", description: MESSAGES.ERROR.GENERIC });
    } finally {
      setIsConfirmingDelete(false);
    }
  }, [pendingDelete, editor, toast]);

  /**
   * Whether the dashed add row is expanded. Collapsed is the resting state —
   * the design's progressive disclosure — so the editor does not end on a
   * permanently open, permanently empty input.
   */
  const [isAddOpen, setIsAddOpen] = useState(false);
  const handleAddOpen = useCallback(() => setIsAddOpen(true), []);

  /**
   * Collapse and DISCARD the typed name. Discarding is the point: a collapsed
   * row shows no input, so a name kept behind it would be invisible state that
   * reappears the next time the row is opened.
   */
  const handleAddCancel = useCallback(() => {
    setIsAddOpen(false);
    setAddName("");
  }, []);

  /**
   * Marks that the confirm button is mid-press.
   *
   * Pressing the confirm moves focus off the input, and on web that focus change
   * lands BEFORE the press resolves. So the input's blur used to collapse the
   * row and clear the draft while the press was still in flight, and the press
   * then arrived at an empty name and did nothing at all — pressing Add did not
   * add the stage, while Enter (which never blurs first) did (SE-6915).
   *
   * A ref, not state: it is written on press-in and read by the blur that lands
   * in the same interaction, and a re-render between the two is precisely the
   * behaviour being prevented.
   *
   * It only ever fires on NATIVE. Through the compiled RN-web stack the blur
   * arrives first and takes the button down with the row, so neither press
   * callback runs and this mark is never set — web is covered by the confirm's
   * own focus guard in the View, which stops the blur from happening at all.
   */
  const addSubmitIntentRef = useRef(false);

  const handleAddSubmitPressIn = useCallback(() => {
    addSubmitIntentRef.current = true;
  }, []);

  /**
   * Clears the mark when the press finishes or is aborted (pointer released off
   * the button). An aborted press leaves the row expanded with the draft intact
   * — the input has lost focus, so the next blur cannot re-run this guard.
   */
  const handleAddSubmitPressOut = useCallback(() => {
    addSubmitIntentRef.current = false;
  }, []);

  /**
   * Blur on the add input: collapse and discard, EXCEPT when the blur came from
   * pressing the confirm. Abandoning the draft by clicking anywhere else still
   * collapses the row.
   */
  const handleAddBlur = useCallback(() => {
    if (addSubmitIntentRef.current) return;
    handleAddCancel();
  }, [handleAddCancel]);

  const handleAddSubmit = useCallback(async () => {
    // Also cleared here, not only on press-out: a submit reached by Enter never
    // sets the mark, and a press whose element unmounts mid-flight never
    // delivers its press-out.
    addSubmitIntentRef.current = false;
    const trimmed = addName.trim();
    if (trimmed.length === 0) return;
    setIsAdding(true);
    try {
      await editor.addStage(trimmed);
      setAddName("");
      // A successful add collapses the row back to its resting state; adding
      // several stages in a row is a second press, not a sticky form.
      setIsAddOpen(false);
    } catch {
      toast.show({ action: "danger", description: MESSAGES.ERROR.GENERIC });
    } finally {
      setIsAdding(false);
    }
  }, [addName, editor, toast]);

  const rowHandlers = useMemo(
    () => ({
      onStartRename: handleStartRename,
      onChangeName: setEditNameValue,
      onSaveRename: handleSaveRename,
      onCancelRename: handleCancelRename,
      onToggleProtected: handleToggleProtected,
      onToggleTerminal: handleToggleTerminal,
      onChangeTarget: handleChangeTarget,
      onMoveUp: handleMoveUp,
      onMoveDown: handleMoveDown,
      onDelete: handleDelete,
    }),
    [
      handleStartRename,
      handleSaveRename,
      handleCancelRename,
      handleToggleProtected,
      handleToggleTerminal,
      handleChangeTarget,
      handleMoveUp,
      handleMoveDown,
      handleDelete,
    ]
  );

  const rowData = useMemo<StageEditorRowData[]>(
    () =>
      stages.map((stage, index) => {
        // The row's count pill, Guarded badge, and protection notice all read
        // the SAME two sets the delete guard reads, so what a row promises and
        // what the confirm dialog enforces can never disagree.
        const cardCount = countCardsInStage(cards, stage.id);
        const ruleCount = findRulesReferencingStage(rules, stage.id).length;
        return {
          stageId: stage.id,
          name: stage.name,
          description: stageGlossaryFor(pipelineTemplate, stage.name),
          isProtected: stage.isProtected,
          isTerminal: stage.isTerminal,
          cardCount,
          ruleCount,
          protectionNotice: buildStageProtectionNotice({
            name: stage.name,
            isProtected: stage.isProtected,
            isTerminal: stage.isTerminal,
            cardCount,
            ruleCount,
          }),
          timeTargetDays: stage.timeTargetDays,
          isFirst: index === 0,
          isLast: index === stages.length - 1,
          isBusy: busyIds.has(stage.id),
          isEditingName: editingStageId === stage.id,
          editNameValue: editingStageId === stage.id ? editNameValue : "",
        };
      }),
    [
      stages,
      cards,
      rules,
      pipelineTemplate,
      busyIds,
      editingStageId,
      editNameValue,
    ]
  );

  const deleteState = useMemo<StageEditorDeleteState>(
    () => ({
      isOpen: pendingDelete !== null,
      stageName: pendingDelete?.stageName ?? "",
      plan: pendingDelete?.plan ?? null,
      isConfirming: isConfirmingDelete,
    }),
    [pendingDelete, isConfirmingDelete]
  );

  return (
    <StageEditorView
      isOpen={isEditorOpen}
      onClose={handleClose}
      pipelineName={boardQuery.data?.getKanbanBoard?.name ?? ""}
      loading={phasesQuery.loading}
      isError={Boolean(phasesQuery.error)}
      stages={rowData}
      rowHandlers={rowHandlers}
      addName={addName}
      onAddNameChange={setAddName}
      onAddSubmit={handleAddSubmit}
      onAddSubmitPressIn={handleAddSubmitPressIn}
      onAddSubmitPressOut={handleAddSubmitPressOut}
      isAdding={isAdding}
      isAddOpen={isAddOpen}
      onAddOpen={handleAddOpen}
      onAddBlur={handleAddBlur}
      deleteState={deleteState}
      onConfirmDelete={handleConfirmDelete}
      onCancelDelete={handleCancelDelete}
    />
  );
};

StageEditorContainer.displayName = "StageEditorContainer";

export default memo(StageEditorContainer);
